import { ChevronDown, Loader2, Search, Check } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import Card from "./card";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import zipCodes from "../../data/tarrant-county-zip-codes.json";

const STATIC_CARD_IMAGE = "/assets/images/broken.png";
const STATIC_DESCRIPTION = "Empowering communities through collaborative initiatives.";
const PAGE_SIZE = 8;
const SEARCH_DEBOUNCE_MS = 400;

const sortOptions = [
  { label: "Least Active", value: "least-active" },
  { label: "Newest", value: "newest" },
  { label: "Oldest", value: "oldest" },
];

export default function MainNonProfit() {
  const locationDropdownRef = useRef(null);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const servicesDropdownRef = useRef(null);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedZips, setSelectedZips] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [sortBy] = useState(sortOptions[0].value);
  const [nonprofits, setNonprofits] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setLocationDropdownOpen(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setServicesDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    fetch(`${BASE_URL}${API_ENDPOINTS.GET_LOVS_PUBLIC}?key=NONPROFIT_SERVICES`)
      .then((r) => r.json())
      .then((result) => {
        const services = result?.data || result;
        if (Array.isArray(services)) setServiceOptions(services);
      })
      .catch(() => {});
  }, []);

  const fetchNonprofits = useCallback(async () => {
    setIsLoading(true);

    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
      sortBy,
    });

    if (searchTerm) params.set("search", searchTerm);
    if (selectedZips.length > 0 && selectedZips.length < zipCodes.length) {
      selectedZips.forEach((zip) => params.append("location", zip));
    }
    if (selectedServices.length > 0 && selectedServices.length < serviceOptions.length) {
      selectedServices.forEach((id) => params.append("services", id));
    }

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_DIRECTORY}?${params.toString()}`
      );
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = result?.message || "Unable to load nonprofits.";
        throw new Error(message);
      }

      const payload = result?.data ?? null;
      const records = Array.isArray(payload?.data)
        ? payload.data.map((item) => ({
            ...item,
            image: item.profilePictureUrl || STATIC_CARD_IMAGE,
            desc: item.mission || STATIC_DESCRIPTION,
          }))
        : [];

      setNonprofits(records);
      setMeta(payload?.meta ?? null);
      if (records.length) {
        setSelectedId(records[0].id);
      } else {
        setSelectedId(null);
      }
    } catch (error) {
      toast.error(error?.message || "Unable to load nonprofits.");
      setNonprofits([]);
      setMeta(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedZips, page, searchTerm, selectedServices, sortBy, serviceOptions.length]);

  useEffect(() => {
    fetchNonprofits();
  }, [fetchNonprofits]);

  const handleNavigate = (id) => {
    if (id) {
      setSelectedId(id);
      navigate(`/dashboard/collaborate/${id}`);
    }
  };

  const toggleZip = (zip) => {
    setSelectedZips((prev) =>
      prev.includes(zip) ? prev.filter((z) => z !== zip) : [...prev, zip]
    );
    setPage(1);
  };

  const toggleService = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
    setPage(1);
  };

  const handlePageChange = (direction) => {
    if (direction === "prev" && meta?.hasPreviousPage) {
      setPage((prev) => Math.max(1, prev - 1));
    }
    if (direction === "next" && meta?.hasNextPage) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div className="w-[95%] mx-auto my-8">
      <div>
        <h1 className="font-popinsBold text-2xl">Explore NGOs & Collaborate</h1>
        <p className="font-popinsRegular text-textColor mt-1 text-sm">
          Find trusted community organizations and build meaningful partnerships.
        </p>
      </div>
      <div className="w-[80%]">
        <div className="relative mt-7">
          <input
            type="text"
            placeholder="Search by name or service..."
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              setPage(1);
            }}
            className="peer w-full font-popinsMedium rounded-md border-2 border-border bg-[#F0F2F5] pl-9 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          />
          <div className="absolute left-3 top-3">
            <Search size={18} color="#61708A" />
          </div>
        </div>
        <div className="flex space-x-4 mt-5">
          {/* Location (ZIP) Multiselect */}
          <div className="relative" ref={locationDropdownRef}>
            <button
              type="button"
              onClick={() => setLocationDropdownOpen((o) => !o)}
              className="min-w-[140px] max-w-[280px] h-[38px] px-3 border rounded-md text-[#121417] font-popinsMedium cursor-pointer bg-[#F0F2F5] shadow-sm hover:bg-gray-100 focus:outline-none flex items-center relative"
            >
              <div className="flex-1 flex gap-1 items-center min-w-0 pr-6 overflow-hidden">
                {selectedZips.length === 0 ? (
                  <span className="text-sm text-[#121417] whitespace-nowrap">Location (ZIP)</span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-themeColor/10 text-themeColor rounded-md text-xs font-popinsMedium whitespace-nowrap flex-shrink-0">
                      {selectedZips[0]}
                    </span>
                    {selectedZips.length > 1 && (
                      <span className="text-xs font-popinsMedium text-black/50 whitespace-nowrap flex-shrink-0">
                        +{selectedZips.length - 1}
                      </span>
                    )}
                  </>
                )}
              </div>
              <ChevronDown
                size={16}
                className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[#121417] transition-transform ${locationDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>
            {locationDropdownOpen && (
              <div className="absolute mt-1 w-44 max-h-60 overflow-y-auto bg-white border border-border rounded-md shadow-lg z-50">
                {(() => {
                  const allSelected = zipCodes.length > 0 && zipCodes.every((z) => selectedZips.includes(z));
                  return (
                    <div
                      className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 border-b border-black/10"
                      onClick={() => { setSelectedZips(allSelected ? [] : [...zipCodes]); setPage(1); }}
                    >
                      <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${allSelected ? "border-themeColor bg-themeColor" : "border-gray-300"}`}>
                        {allSelected && <Check size={11} className="text-white" />}
                      </div>
                      <span className="font-popinsRegular text-sm text-black/60">Select All</span>
                    </div>
                  );
                })()}
                {zipCodes.map((zip) => {
                  const isSelected = selectedZips.includes(zip);
                  return (
                    <div
                      key={zip}
                      onClick={() => toggleZip(zip)}
                      className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                    >
                      <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? "border-themeColor bg-themeColor" : "border-gray-300"}`}>
                        {isSelected && <Check size={11} className="text-white" />}
                      </div>
                      <span className="font-popinsRegular text-sm text-black/60">{zip}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Services Multiselect */}
          <div className="relative" ref={servicesDropdownRef}>
            <button
              type="button"
              onClick={() => setServicesDropdownOpen((o) => !o)}
              className="min-w-[160px] max-w-[280px] h-[38px] px-3 border rounded-md text-[#121417] font-popinsMedium cursor-pointer bg-[#F0F2F5] shadow-sm hover:bg-gray-100 focus:outline-none flex items-center relative"
            >
              <div className="flex-1 flex gap-1 items-center min-w-0 pr-6 overflow-hidden">
                {selectedServices.length === 0 ? (
                  <span className="text-sm text-[#121417] whitespace-nowrap">Services Offered</span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-themeColor/10 text-themeColor rounded-md text-xs font-popinsMedium whitespace-nowrap flex-shrink-0">
                      {serviceOptions.find((s) => s.id === selectedServices[0])?.name ?? "…"}
                    </span>
                    {selectedServices.length > 1 && (
                      <span className="text-xs font-popinsMedium text-black/50 whitespace-nowrap flex-shrink-0">
                        +{selectedServices.length - 1}
                      </span>
                    )}
                  </>
                )}
              </div>
              <ChevronDown
                size={16}
                className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[#121417] transition-transform ${servicesDropdownOpen ? "rotate-180" : ""}`}
              />
            </button>
            {servicesDropdownOpen && (
              <div className="absolute mt-1 w-56 max-h-60 overflow-y-auto bg-white border border-border rounded-md shadow-lg z-50">
                {serviceOptions.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-400 italic">Loading…</div>
                ) : (
                  <>
                    {(() => {
                      const allSelected = serviceOptions.length > 0 && serviceOptions.every((o) => selectedServices.includes(o.id));
                      return (
                        <div
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2 border-b border-black/10"
                          onClick={() => { setSelectedServices(allSelected ? [] : serviceOptions.map((o) => o.id)); setPage(1); }}
                        >
                          <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${allSelected ? "border-themeColor bg-themeColor" : "border-gray-300"}`}>
                            {allSelected && <Check size={11} className="text-white" />}
                          </div>
                          <span className="font-popinsRegular text-sm text-black/60">Select All</span>
                        </div>
                      );
                    })()}
                    {serviceOptions.map((opt) => {
                      const isSelected = selectedServices.includes(opt.id);
                      return (
                        <div
                          key={opt.id}
                          onClick={() => toggleService(opt.id)}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                        >
                          <div className={`w-4 h-4 border-2 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? "border-themeColor bg-themeColor" : "border-gray-300"}`}>
                            {isSelected && <Check size={11} className="text-white" />}
                          </div>
                          <span className="font-popinsRegular text-sm text-black/60">{opt.name}</span>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

        </div>
      </div>
      <div className="flex flex-wrap gap-5 w-full mt-14">
        {isLoading ? (
          <div className="w-full flex justify-center py-10">
            <Loader2 className="animate-spin text-themeColor" size={28} />
          </div>
        ) : nonprofits.length ? (
          nonprofits.map((item) => (
            <div key={item.id} className="w-full sm:w-[48%] lg:w-[23%] flex">
              <Card
                data={item}
                isSelected={item.id === selectedId}
                onClick={() => handleNavigate(item.id)}
              />
            </div>
          ))
        ) : (
          <p className="font-popinsMedium text-center w-full text-black/60">
            No nonprofits found. Try adjusting your filters.
          </p>
        )}
      </div>
      {meta && nonprofits.length > 0 && (
        <div className="flex justify-end items-center gap-4 mt-6">
          <button
            className="px-4 py-2 border rounded-md font-popinsMedium text-sm disabled:opacity-40"
            onClick={() => handlePageChange("prev")}
            disabled={!meta.hasPreviousPage}
          >
            Previous
          </button>
          <span className="font-popinsMedium text-sm text-black/60">
            Page {meta.page} of {meta.totalPages}
          </span>
          <button
            className="px-4 py-2 border rounded-md font-popinsMedium text-sm disabled:opacity-40"
            onClick={() => handlePageChange("next")}
            disabled={!meta.hasNextPage}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
